import { Tabs as TabsPrimitive } from "@base-ui/react/tabs";
import { merge } from "./merge";

/**
 * Underlined tabs as on Figma 7.17: plain text tabs with one indicator that slides under the
 * active one. Base UI owns the roving tab stop, arrow keys, Home/End and the tab-to-panel
 * wiring; nothing here re-implements them.
 */
function TabsRoot(props: TabsPrimitive.Root.Props) {
  return <TabsPrimitive.Root {...props} data-slot="tabs" />;
}

/**
 * The indicator belongs to the list rather than to a tab, so it can travel between tabs instead of
 * being redrawn under each. Base UI measures the active tab and publishes `--active-tab-left` and
 * `--active-tab-width`; the transition is on those two alone, and is dropped for a reader who has
 * asked for less motion — the indicator then lands on the new tab without gliding.
 *
 * `overflow-x-auto` keeps a long set of tabs on one line on a phone instead of wrapping them. A
 * scrolling box clips whatever is drawn outside it, focus rings included, so the list keeps a
 * ring's width of room on every side; it reaches that far past the wrapper on the left and right,
 * and the wrapper draws the baseline, so tabs and baseline still line up with the content.
 */
function TabsList({ className, children, ...props }: TabsPrimitive.List.Props) {
  return (
    <div data-slot="tabs-baseline" className="border-b border-border">
      <TabsPrimitive.List
        {...props}
        data-slot="tabs-list"
        className={merge(
          "relative -mx-(--tabs-ring) flex gap-6 overflow-x-auto p-(--tabs-ring) [--tabs-ring:3px]",
          className,
        )}
      >
        {children}
        <TabsPrimitive.Indicator
          data-slot="tabs-indicator"
          className="absolute bottom-0 left-(--active-tab-left) h-0.5 w-(--active-tab-width) bg-foreground transition-[left,width] duration-normal ease-in-out motion-reduce:transition-none"
        />
      </TabsPrimitive.List>
    </div>
  );
}

/**
 * The active tab is `data-active` in Base UI 1.8, not `data-selected`. Weight changes with
 * selection, so the label is not in the transition list: a title stepping 400 to 500 mid-fade
 * would reflow its own line. The list's padding comes out of the tab's height, so the row stays
 * 40px and the label sits where it did.
 */
function TabsTab({ className, ...props }: TabsPrimitive.Tab.Props) {
  return (
    <TabsPrimitive.Tab
      {...props}
      data-slot="tabs-tab"
      className={merge(
        "flex h-[calc(--spacing(10)-2*var(--tabs-ring))] shrink-0 cursor-pointer items-center gap-1.5 text-(length:--text-ui) leading-(--text-ui--line-height) text-muted-foreground outline-none select-none transition-colors duration-fast ease-out hover:text-foreground focus-visible:ring-3 focus-visible:ring-(--focus-halo) data-active:font-medium data-active:text-foreground data-disabled:pointer-events-none data-disabled:opacity-50 motion-reduce:transition-none",
        className,
      )}
    />
  );
}

function TabsPanel({ className, ...props }: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel
      {...props}
      data-slot="tabs-panel"
      className={merge("pt-5 outline-none", className)}
    />
  );
}

/** Supported Tabs parts only; the indicator is part of the list, so there is no part to forget. */
export const Tabs = { Root: TabsRoot, List: TabsList, Tab: TabsTab, Panel: TabsPanel };
